"use client"
import { useEffect, useRef } from "react"
import { googleConsentSignals, useConsent } from "@/lib/consent"

interface GoogleAnalyticsProps {
  measurementId: string
  nonce?: string
}

// Google Analytics 4 (gtag.js) — only after the visitor accepts Analytics in the cookie banner
// (src/lib/consent.ts), with Google Consent Mode v2 signals from that choice. The gtag.js script is
// inserted by this nonce'd module (CSP 'strict-dynamic' trusts it; the collect endpoints are
// allow-listed in connect-src/img-src). gtag is exposed on window so conversion events can fire it.
//
// The 168 KB gtag bundle used to be requested the moment React hydrated, competing with the
// hero image for bandwidth on a phone (it showed up in every LCP trace of the 2026-09-01
// audit). It now waits for the window `load` event and then an idle slot. `gtag` and the
// dataLayer are stubbed synchronously, so any conversion event fired before the script
// arrives is queued, not lost — and the page_view still counts every visit.
export function GoogleAnalytics({ measurementId, nonce = "" }: GoogleAnalyticsProps) {
  const loaded = useRef(false)
  const allowed = useConsent("analytics")

  useEffect(() => {
    if (!allowed || loaded.current) return
    loaded.current = true

    const w = window as unknown as {
      dataLayer: unknown[]
      gtag: (...args: unknown[]) => void
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
    }
    w.dataLayer = w.dataLayer || []
    // gtag.js only reads the `arguments` object itself: pushing an array (`(...args) => push(args)`)
    // is silently ignored, which sent 0 hits (proved 2026-10-08 with a headless network trace).
    const gtag = function () {
      // eslint-disable-next-line prefer-rest-params
      w.dataLayer.push(arguments)
    } as (...args: unknown[]) => void
    w.gtag = gtag
    gtag("consent", "default", googleConsentSignals())
    gtag("js", new Date())
    gtag("config", measurementId)

    let cancelled = false
    const inject = () => {
      if (cancelled || document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) return
      const s = document.createElement("script")
      s.async = true
      s.src = "https://www.googletagmanager.com/gtag/js?id=" + measurementId
      if (nonce) s.nonce = nonce
      document.head.appendChild(s)
    }
    const whenIdle = () => {
      if (typeof w.requestIdleCallback === "function") w.requestIdleCallback(inject, { timeout: 4000 })
      else setTimeout(inject, 1500)
    }
    if (document.readyState === "complete") whenIdle()
    else window.addEventListener("load", whenIdle, { once: true })

    return () => {
      cancelled = true
      window.removeEventListener("load", whenIdle)
    }
  }, [measurementId, nonce, allowed])

  return null
}
